import { useMutation } from "@apollo/client";
import { Button } from "@chakra-ui/react";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
} from "gql/graphql";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import {
  RestoreIcon,
  CircleMinusIcon,
  WarningIcon,
  GreenCheckIcon,
  TrashOutlineIcon,
} from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";
import { getDistributionGroupMention } from "modules/utils/Functions";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";

type ButtonType = "profile" | "table";

interface IProps {
  distributionGroup: InternalAdminDistributionGroupFragment;
  type?: ButtonType;
  refetch: () => void;
}

const InternalAdminDistributionGroupArchiveButton = ({
  distributionGroup,
  type = "profile",
  refetch,
}: IProps) => {
  const { openModal } = useOverlay();
  const [internalAdminEditDistributionGroupMutationAPI] = useMutation<
    InternalAdminEditDistributionGroupMutation,
    InternalAdminEditDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const onArchiveGroupClicked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      openModal({
        type: MODAL_TYPE.CONFIRM,
        helpModal: {
          icon: <WarningIcon boxSize={14} />,
          title: `Archive Group?`,
          message: `Are you sure you want to archive: ${getDistributionGroupMention(distributionGroup)}?`,
        },
        onSaveCallback: async () => {
          await internalAdminEditDistributionGroupMutationAPI({
            variables: {
              organizationId: distributionGroup.organizationId,
              id: distributionGroup.id,
              input: {
                active: false,
              },
            },
          });
          refetch();
        },
      });
    },
    [
      distributionGroup,
      internalAdminEditDistributionGroupMutationAPI,
      openModal,
      refetch,
    ],
  );

  const onUnarchiveGroupClicked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      openModal({
        type: MODAL_TYPE.CONFIRM,
        helpModal: {
          icon: <GreenCheckIcon boxSize={14} />,
          title: "Unarchive Group?",
          message: `Are you sure you want to unarchive: ${getDistributionGroupMention(distributionGroup)}?`,
        },
        onSaveCallback: async () => {
          await internalAdminEditDistributionGroupMutationAPI({
            variables: {
              organizationId: distributionGroup.organizationId,
              id: distributionGroup.id,
              input: {
                active: true,
              },
            },
          });
          refetch();
        },
      });
    },
    [
      distributionGroup,
      internalAdminEditDistributionGroupMutationAPI,
      openModal,
      refetch,
    ],
  );

  if (distributionGroup.active && type === "profile") {
    return (
      <Button
        size="xxs"
        leftIcon={<TrashOutlineIcon boxSize={5} fill={colors.gray40} />}
        onClick={onArchiveGroupClicked}
        variant="ghost"
        color={colors.gray40}
        w="fit-content"
        alignSelf="flex-end"
        fontSize={12}
      >
        Archive Group
      </Button>
    );
  }

  if (!distributionGroup.active && type === "profile") {
    return (
      <Button
        height={5}
        p={0}
        m={0}
        leftIcon={<RestoreIcon boxSize={5} fill={colors.gray40} />}
        onClick={onUnarchiveGroupClicked}
        variant="ghost"
        color={colors.gray40}
        w="fit-content"
        alignSelf="flex-end"
        fontSize={12}
      >
        Unarchive Group
      </Button>
    );
  }

  if (distributionGroup.active) {
    return (
      <CircularIconButton
        aria-label="archive distribution group"
        variant="ghost"
        icon={<CircleMinusIcon boxSize={5} fill={colors.gray40} />}
        onClick={onArchiveGroupClicked}
      />
    );
  }

  return (
    <CircularIconButton
      aria-label="archive distribution group"
      variant="ghost"
      icon={<RestoreIcon boxSize={5} fill={colors.gray40} />}
      onClick={onUnarchiveGroupClicked}
    />
  );
};

export default memo(InternalAdminDistributionGroupArchiveButton);
